import * as yup from "yup";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from "@mui/material";
import { capitalize, titleCase } from "portal/utils/strings";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { ChecklistDefinition } from "protos/rtc/jobs";
import { Field, Form, Formik } from "formik";
import { getStartupChecklistDefinitionPath } from "portal/utils/routing";
import {
  GridColDef,
  GridInitialState as GridInitialStatePremium,
} from "@mui/x-data-grid-premium";
import { Header } from "portal/components/header/Header";
import { NoScroll, Page } from "portal/components/Page";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { TextField } from "formik-mui";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import React, {
  FunctionComponent,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

const checklistValidationSchema = yup.object({
  name: yup.string().required(),
  description: yup.string(),
});

const getRowId = (checklist: ChecklistDefinition): number => {
  return checklist.id;
};

interface NewChecklistFormProps {
  onSubmit: (values: { name: string; description: string }) => Promise<void>;
  onCancel: () => void;
}

const NewChecklistForm: FunctionComponent<NewChecklistFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 p-4">
      <Formik
        initialValues={{ name: "", description: "" }}
        validationSchema={checklistValidationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          await onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ submitForm, handleReset, isSubmitting, isValid, dirty }) => (
          <Form className="flex flex-col gap-4">
            <Field
              component={TextField}
              name="name"
              type="text"
              label={capitalize(t("utils.descriptors.name"))}
              fullWidth
            />
            <Field
              component={TextField}
              name="description"
              type="text"
              label={capitalize(t("utils.descriptors.description_one"))}
              fullWidth
            />
            <div className="flex">
              <Button
                fullWidth
                variant="contained"
                disabled={!isValid || isSubmitting || !dirty}
                type="submit"
                onClick={submitForm}
              >
                {t("utils.actions.create")}
              </Button>
              <Button
                fullWidth
                color="info"
                onClick={() => {
                  onCancel();
                  handleReset();
                }}
              >
                {t("utils.actions.cancel")}
              </Button>
            </div>
            {isSubmitting && (
              <LinearProgress className="w-full" variant="indeterminate" />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

function useNewChecklistDialog(): {
  renderDialog: () => ReactNode;
  open: () => void;
  close: () => void;
} {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [createChecklist] = useMutationPopups(
    rtcJobsApi.endpoints.createChecklistDefinition.useMutation()
  );
  const { t } = useTranslation();

  const handleCreateChecklist = useCallback(
    async (values: { name: string; description: string }): Promise<void> => {
      const result = await createChecklist(values);
      if (!result.error) {
        setIsOpen(false);
      }
    },
    [createChecklist]
  );

  const handleCancelChecklist = useCallback((): void => {
    setIsOpen(false);
  }, []);

  return {
    renderDialog: () => (
      <Dialog open={isOpen} fullWidth>
        <DialogTitle>
          {titleCase(
            t("utils.actions.newLong", {
              subject: t("models.autotractor.checklists.checklist_one"),
            })
          )}
        </DialogTitle>
        <DialogContent>
          <NewChecklistForm
            onCancel={handleCancelChecklist}
            onSubmit={handleCreateChecklist}
          />
        </DialogContent>
      </Dialog>
    ),
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };
}

export const StartupChecklistList: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    renderDialog: renderNewChecklistDialog,
    open: openNewChecklistDialog,
  } = useNewChecklistDialog();
  const { data, isLoading } =
    rtcJobsApi.endpoints.listStartupChecklistDefinitions.useQuery();

  const rows = useMemo(() => {
    return data?.checklistDefinitions ?? [];
  }, [data]);

  const columns = useMemo<GridColDef<ChecklistDefinition>[]>(() => {
    const defaultColumn: Partial<GridColDef> = {
      disableColumnMenu: true,
      sortable: true,
      flex: 1,
    };
    return [
      {
        ...defaultColumn,
        field: "name",
        headerName: capitalize(t("utils.descriptors.name")),
        valueGetter: (value, row) => row.content?.name ?? "",
      },
      {
        ...defaultColumn,
        field: "description",
        headerName: capitalize(t("utils.descriptors.description_one")),
        flex: 2,
        valueGetter: (value, row) => row.content?.description ?? "",
      },
    ];
  }, [t]);

  const initialState: GridInitialStatePremium = {
    sorting: {
      sortModel: [{ field: "name", sort: "asc" }],
    },
  };

  return (
    <>
      {renderNewChecklistDialog()}
      <Header
        title={capitalize(
          t("models.autotractor.checklists.startup_checklist_other")
        )}
      >
        <Button
          className="text-white ml-auto"
          startIcon={<AddIcon />}
          onClick={openNewChecklistDialog}
        >
          <span className="hidden sm:inline">
            {titleCase(
              t("utils.actions.newLong", {
                subject: t("models.autotractor.checklists.checklist_one"),
              })
            )}
          </span>
          <span className="sm:hidden">{t("utils.actions.new")}</span>
        </Button>
      </Header>
      <Page>
        <NoScroll>
          <CarbonDataGrid<ChecklistDefinition>
            columns={columns}
            rows={rows}
            loading={isLoading}
            getRowId={getRowId}
            initialState={initialState}
            getRowClassName={() => "cursor-pointer"}
            onRowClick={({ row }) =>
              navigate(getStartupChecklistDefinitionPath(row.id.toString()))
            }
            disableRowSelectionOnClick
            hideFooter
          />
        </NoScroll>
      </Page>
    </>
  );
};
